import React, { useCallback, useEffect, useRef, useState } from 'react';
import { examApi } from '../services/api';
import { formatDateTime } from '../utils/format';
import { getApiErrorMessage } from '../utils/examStart';
import { Button } from './ui';
import { useAuth } from '../hooks/useAuth';
import { useUpgrade } from '../hooks/useUpgrade';
import './ExamInsightPanel.css';

interface InsightStats {
  score: number;
  passingScore: number;
  answered: number;
  total: number;
  correct: number;
  unanswered: number;
  domains: Array<{ domain: string; name: string; weight: number; total: number; correct: number; rate: number | null }>;
  unassigned: { total: number; correct: number };
  difficulty: Array<{ level: 1 | 2 | 3; total: number; correct: number; rate: number | null }>;
  keywords: Array<{ keyword: string; wrong: number; total: number }>;
  trend: Array<{ sessionId: string; score: number; completedAt: string }>;
  lostPoints?: Array<{ domain: string; name: string; points: number }>;
  references?: Array<{ url: string; title?: string; count: number; keywords: string[] }>;
  timeTakenMinutes?: number;
}

interface InsightAi {
  headline?: string;
  summary: string;
  readiness: { level: 'ready' | 'close' | 'not_ready'; comment: string };
  domainNotes?: Array<{ domain: string; verdict: 'strong' | 'watch' | 'weak'; comment: string }>;
  misconceptions?: Array<{ title: string; evidence: string; correction: string; services: string[] }>;
  decisionRules?: Array<{ signal: string; choose: string; avoid?: string }>;
  weakAreas: Array<{ title: string; domain?: string; why: string; topics: string[] }>;
  keywordNotes: Array<{ keyword: string; note: string }>;
  studyPlan: Array<{ priority: number; title: string; domain?: string; effort?: string; actions: string[] }>;
  nextSteps: string[];
}

interface Insight {
  status: 'queued' | 'running' | 'ready' | 'failed';
  requested_at: string;
  generated_at?: string;
  model?: string;
  stats?: InsightStats;
  ai?: InsightAi;
  error?: string;
}

const READINESS: Record<InsightAi['readiness']['level'], { label: string; tone: string }> = {
  ready: { label: '합격권', tone: 'ready' },
  close: { label: '합격선 근접', tone: 'close' },
  not_ready: { label: '보강 필요', tone: 'not-ready' },
};

const POLL_MS = 5000;
const POLL_LIMIT_MS = 6 * 60_000;

/**
 * AI 학습 분석 (AI Power User·관리자 전용).
 * 실전 시험을 제출하면 서버가 백그라운드로 분석하고, 이 패널은 끝날 때까지 기다렸다가 결과를 보여 줍니다.
 */
const ExamInsightPanel: React.FC<{ sessionId: string; practice?: boolean }> = ({ sessionId, practice = false }) => {
  const [eligible, setEligible] = useState<boolean | null>(null);
  const [insight, setInsight] = useState<Insight | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [slow, setSlow] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const { user } = useAuth();
  const { requireAiPower } = useUpgrade();
  // 체험 시작·구독으로 권한이 바뀌면 다시 불러옵니다
  const powerKey = `${user?.ai_power?.aiPower ? 1 : 0}`;
  const pollStarted = useRef<number>(0);

  const load = useCallback(async () => {
    try {
      const response = await examApi.getInsight(sessionId) as { success: boolean; data?: { eligible: boolean; insight?: Insight | null } };
      setEligible(Boolean(response.data?.eligible));
      setInsight(response.data?.insight || null);
      return response.data?.insight || null;
    } catch (loadError) {
      setError(getApiErrorMessage(loadError) || 'AI 학습 분석을 불러오지 못했습니다.');
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, powerKey]);

  const poll = useCallback(async () => {
    const latest = await load();
    if (latest && (latest.status === 'queued' || latest.status === 'running')) {
      if (Date.now() - pollStarted.current > POLL_LIMIT_MS) {
        setSlow(true);
        return;
      }
      timer.current = window.setTimeout(poll, POLL_MS);
    }
  }, [load]);

  useEffect(() => {
    pollStarted.current = Date.now();
    poll();
    return () => window.clearTimeout(timer.current);
  }, [poll]);

  const request = async () => {
    setRequesting(true);
    setError(null);
    setSlow(false);
    try {
      await examApi.requestInsight(sessionId);
      pollStarted.current = Date.now();
      window.clearTimeout(timer.current);
      await poll();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError) || 'AI 학습 분석을 시작하지 못했습니다.');
    } finally {
      setRequesting(false);
    }
  };

  if (eligible === null && !error) return null;

  if (eligible === false) {
    return (
      <section className="insight insight--teaser" aria-label="AI 학습 분석">
        <div className="insight__empty">
          <div>
            <p className="insight__eyebrow">AI Power User</p>
            <h2>학습 진단 리포트</h2>
            <p>영역별 감점 분석, 오답 패턴, 시험장 판단 기준, 우선순위별 보강 계획을 시험마다 정리해 드립니다.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => requireAiPower('AI 학습 진단 리포트')}>진단 리포트 받기</Button>
        </div>
      </section>
    );
  }

  const running = insight && (insight.status === 'queued' || insight.status === 'running');

  return (
    <section className="insight" aria-label="AI 학습 분석">
      <header className="insight__head">
        <div>
          <p className="insight__eyebrow">AI Power User</p>
          <h2>학습 진단 리포트</h2>
          {insight?.status === 'ready' && insight.generated_at && (
            <p className="insight__meta">{formatDateTime(insight.generated_at)} 작성 · {insight.model}</p>
          )}
        </div>
        {insight?.status === 'ready' && insight.ai && (
          <span className={`insight__readiness insight__readiness--${READINESS[insight.ai.readiness.level].tone}`}>
            {READINESS[insight.ai.readiness.level].label}
          </span>
        )}
      </header>

      {error && <p className="insight__error" role="alert">{error}</p>}

      {!insight && (
        <div className="insight__empty">
          <p>{practice ? '연습 모드 시험은 자동으로 분석하지 않습니다. 필요하면 지금 진단 리포트를 받아 보세요. (약 2~3분)' : '이 시험은 아직 진단 리포트가 없습니다. 기능 추가 전에 제출한 시험이면 지금 받아 보세요. (약 2~3분)'}</p>
          <Button variant="secondary" size="sm" onClick={request} loading={requesting}>진단 리포트 받기</Button>
        </div>
      )}

      {running && (
        <div className="insight__running" role="status" aria-live="polite">
          <span className="insight__spinner" aria-hidden="true" />
          <p>
            이번 시험의 진단 리포트를 작성하고 있습니다. 보통 2~3분 걸리고, 이 화면을 닫아도 분석은 계속됩니다.
            {slow && ' 평소보다 오래 걸리고 있어요. 잠시 뒤 새로고침해 주세요.'}
          </p>
        </div>
      )}

      {insight?.status === 'failed' && (
        <div className="insight__empty">
          <p>AI 학습 분석에 실패했습니다.{insight.error ? ` (${insight.error})` : ''}</p>
          <Button variant="secondary" size="sm" onClick={request} loading={requesting}>다시 분석</Button>
        </div>
      )}

      {insight?.status === 'ready' && insight.stats && insight.ai && (
        <InsightBody stats={insight.stats} ai={insight.ai} onRerun={request} rerunning={requesting} />
      )}
    </section>
  );
};

const VERDICT: Record<'strong' | 'watch' | 'weak', string> = { strong: '안정', watch: '주의', weak: '취약' };

const signed = (value: number) => `${value > 0 ? '+' : ''}${value}`;

const InsightBody: React.FC<{ stats: InsightStats; ai: InsightAi; onRerun: () => void; rerunning: boolean }> = ({ stats, ai, onRerun, rerunning }) => {
  const scoredDomains = stats.domains.filter((domain) => domain.total > 0);
  const noteOfDomain = (name: string) => ai.domainNotes?.find((note) => note.domain === name);
  const noteOfKeyword = (keyword: string) => ai.keywordNotes.find((note) => note.keyword === keyword)?.note;
  const lostOf = (id: string) => stats.lostPoints?.find((item) => item.domain === id)?.points ?? 0;
  const topLoss = stats.lostPoints?.[0];
  const gap = stats.score - stats.passingScore;
  const previous = stats.trend.length > 1 ? stats.trend[stats.trend.length - 2].score : null;
  const hard = stats.difficulty.find((item) => item.level === 3 && item.total > 0);
  const misconceptions = ai.misconceptions || [];
  const rules = ai.decisionRules || [];
  let section = 0;
  const next = () => String(++section).padStart(2, '0');

  return (
    <>
      {/* 종합 진단 */}
      <div className="insight__verdict">
        {ai.headline && <p className="insight__headline">{ai.headline}</p>}
        <p className="insight__summary">{ai.summary}</p>
        {ai.readiness.comment && <p className="insight__readiness-comment">{ai.readiness.comment}</p>}
      </div>

      <dl className="insight__kpis">
        <div>
          <dt>점수 / 합격선</dt>
          <dd className="tabular">{stats.score}% <small>/ {stats.passingScore}%</small></dd>
          <span className={`insight__delta ${gap >= 0 ? 'is-up' : 'is-down'}`}>{signed(gap)}%p</span>
        </div>
        <div>
          <dt>최대 감점 영역</dt>
          <dd className="insight__kpi-text">{topLoss ? topLoss.name : '-'}</dd>
          {topLoss && <span className="insight__delta is-down tabular">-{topLoss.points}점</span>}
        </div>
        <div>
          <dt>직전 시험 대비</dt>
          <dd className="tabular">{previous === null ? '첫 응시' : `${previous}% → ${stats.score}%`}</dd>
          {previous !== null && <span className={`insight__delta ${stats.score >= previous ? 'is-up' : 'is-down'}`}>{signed(stats.score - previous)}%p</span>}
        </div>
        <div>
          <dt>고난도 정답률</dt>
          <dd className="tabular">{hard ? `${hard.rate}%` : '-'}</dd>
          {hard && <span className="insight__delta is-neutral tabular">{hard.correct}/{hard.total}문항</span>}
        </div>
      </dl>

      <div className="insight__section">
        <h3><span>{next()}</span>영역별 진단</h3>
        {scoredDomains.length === 0 ? (
          <p className="insight__muted">이 시험 문항에는 출제 영역 정보가 없습니다.</p>
        ) : (
          <div className="insight__table-wrap">
            <table className="insight__table insight__table--domains">
              <thead>
                <tr>
                  <th scope="col">영역 (비중)</th>
                  <th scope="col">정답률</th>
                  <th scope="col" className="is-num">잃은 점수</th>
                  <th scope="col">판정</th>
                </tr>
              </thead>
              <tbody>
                {scoredDomains.map((domain) => {
                  const note = noteOfDomain(domain.name);
                  const below = (domain.rate ?? 0) < stats.passingScore;
                  return (
                    <React.Fragment key={domain.domain}>
                      <tr>
                        <th scope="row">{domain.name} <small>{domain.weight}%</small></th>
                        <td>
                          <div className="insight__rate">
                            <div className="insight__bar" aria-hidden="true">
                              <span className={below ? 'is-below' : 'is-above'} style={{ width: `${domain.rate ?? 0}%` }} />
                              <i style={{ left: `${stats.passingScore}%` }} />
                            </div>
                            <span className="tabular">{domain.rate}% <small>({domain.correct}/{domain.total})</small></span>
                          </div>
                        </td>
                        <td className="is-num tabular">{lostOf(domain.domain) ? `-${lostOf(domain.domain)}` : '0'}</td>
                        <td>{note && <span className={`insight__verdict-chip is-${note.verdict}`}>{VERDICT[note.verdict]}</span>}</td>
                      </tr>
                      {note?.comment && (
                        <tr className="insight__table-note">
                          <td colSpan={4}>{note.comment}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <p className="insight__legend">막대의 세로선은 합격선 {stats.passingScore}%. 잃은 점수는 그 영역에서 틀린 문항이 깎은 점수(문항당 {stats.total ? Math.round(1000 / stats.total) / 10 : 0}점)입니다.</p>
          </div>
        )}
      </div>

      {misconceptions.length > 0 ? (
        <div className="insight__section">
          <h3><span>{next()}</span>오답 패턴 분석</h3>
          <div className="insight__patterns">
            {misconceptions.map((item) => (
              <article key={item.title} className="insight__pattern">
                <h4>{item.title}</h4>
                <dl>
                  <dt>근거</dt>
                  <dd>{item.evidence}</dd>
                  <dt>판단 기준</dt>
                  <dd>{item.correction}</dd>
                </dl>
                {item.services.length > 0 && (
                  <div className="insight__topics">{item.services.map((service) => <span key={service}>{service}</span>)}</div>
                )}
              </article>
            ))}
          </div>
        </div>
      ) : ai.weakAreas.length > 0 && (
        <div className="insight__section">
          <h3><span>{next()}</span>보강이 필요한 부분</h3>
          <div className="insight__patterns">
            {ai.weakAreas.map((area) => (
              <article key={area.title} className="insight__pattern">
                <h4>{area.title}</h4>
                <p>{area.why}</p>
                {area.topics.length > 0 && <div className="insight__topics">{area.topics.map((topic) => <span key={topic}>{topic}</span>)}</div>}
              </article>
            ))}
          </div>
        </div>
      )}

      {rules.length > 0 && (
        <div className="insight__section">
          <h3><span>{next()}</span>시험장 판단 기준</h3>
          <div className="insight__table-wrap">
            <table className="insight__table insight__rules">
              <thead>
                <tr>
                  <th scope="col">지문 신호</th>
                  <th scope="col">고를 것</th>
                  <th scope="col">피할 함정</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.signal}>
                    <td data-label="지문 신호">“{rule.signal}”</td>
                    <td className="is-choose" data-label="고를 것">{rule.choose}</td>
                    <td className="is-avoid" data-label="피할 함정">{rule.avoid || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {stats.keywords.length > 0 && (
        <div className="insight__section">
          <h3><span>{next()}</span>오답 집중 키워드</h3>
          <ul className="insight__keywords">
            {stats.keywords.slice(0, 8).map((keyword) => (
              <li key={keyword.keyword}>
                <div className="insight__kw-head">
                  <strong>{keyword.keyword}</strong>
                  <span className="tabular">{keyword.wrong}/{keyword.total} 오답 · {Math.round((keyword.wrong / keyword.total) * 100)}%</span>
                </div>
                {noteOfKeyword(keyword.keyword) && <p className="insight__note">{noteOfKeyword(keyword.keyword)}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {ai.studyPlan.length > 0 && (
        <div className="insight__section">
          <h3><span>{next()}</span>보강 계획</h3>
          <ol className="insight__plan">
            {[...ai.studyPlan].sort((a, b) => a.priority - b.priority).map((step, index) => (
              <li key={`${step.priority}-${step.title}`}>
                <div className="insight__plan-head">
                  <span className="insight__plan-no">P{index + 1}</span>
                  <strong>{step.title}</strong>
                  {step.effort && <span className="insight__plan-effort">{step.effort}</span>}
                </div>
                {step.domain && <p className="insight__plan-domain">{step.domain}</p>}
                <ul>{step.actions.map((action) => <li key={action}>{action}</li>)}</ul>
              </li>
            ))}
          </ol>
        </div>
      )}

      {stats.references && stats.references.length > 0 && (
        <div className="insight__section">
          <h3><span>{next()}</span>참고 문서</h3>
          <p className="insight__muted insight__muted--top">틀린 문항의 해설 근거로 연결된 AWS 문서입니다.</p>
          <ul className="insight__refs">
            {stats.references.map((reference) => (
              <li key={reference.url}>
                <a href={reference.url} target="_blank" rel="noopener noreferrer">{reference.title || reference.url.replace(/^https?:\/\//, '')}</a>
                <span className="tabular">오답 {reference.count}{reference.keywords.length ? ` · ${reference.keywords.join(', ')}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="insight__footer">
        <div>
          {ai.nextSteps.length > 0 && (
            <>
              <h3>다음 단계</h3>
              <ul className="insight__next">{ai.nextSteps.map((step) => <li key={step}>{step}</li>)}</ul>
            </>
          )}
          {stats.trend.length > 1 && (
            <p className="insight__trend tabular">점수 추이: {stats.trend.map((item) => `${item.score}%`).join(' → ')}</p>
          )}
        </div>
        <Button variant="tertiary" size="sm" onClick={onRerun} loading={rerunning}>다시 분석</Button>
      </div>
      <p className="insight__disclaimer">통계는 이번 응시 기록으로 계산했고, 해석과 계획은 AI가 작성했습니다. 합격을 보장하거나 점수를 예측하지 않습니다.</p>
    </>
  );
};

export default ExamInsightPanel;
